import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ACCENTS = {
  blue: {
    bar: 'from-blue-600 to-indigo-600',
    chip: 'bg-blue-50 text-blue-700 border-blue-200',
    ring: 'hover:border-blue-300'
  },
  indigo: {
    bar: 'from-indigo-600 to-violet-600',
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    ring: 'hover:border-indigo-300'
  },
  rose: {
    bar: 'from-rose-500 to-pink-600',
    chip: 'bg-rose-50 text-rose-700 border-rose-200',
    ring: 'hover:border-rose-300'
  },
  emerald: {
    bar: 'from-emerald-600 to-teal-600',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ring: 'hover:border-emerald-300'
  }
};

const Prices = () => {
  const { t, language } = useTranslation();
  const isRTL = language === 'ar';

  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/pricing`)
      .then(res => { if (!cancelled) setPricing(res.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const label = (value) => value?.[language] || value?.ar || value?.en || '';
  const currency = label(pricing?.currency) || (isRTL ? 'ريال' : 'SR');

  // Static fallbacks so the hero has a real title before the request resolves.
  const heroHeading = label(pricing?.heading) || t('prices.heading');
  const heroSubheading = label(pricing?.subheading) || t('prices.subheading');

  const hero = (
    <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700">
      <div className="absolute inset-0 opacity-10" aria-hidden="true">
        <div className="absolute -top-24 -end-24 w-72 h-72 rounded-full bg-white" />
        <div className="absolute -bottom-32 -start-16 w-96 h-96 rounded-full bg-white" />
      </div>
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 text-center">
        <p className="text-sm sm:text-base font-bold uppercase tracking-[0.2em] text-blue-50 mb-3">
          {heroSubheading}
        </p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
          {heroHeading}
        </h1>
        <div className="w-20 h-1 bg-white/40 rounded-full mx-auto mb-6" />

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          {pricing?.pdf?.available && (
            <a
              href={`${API_URL}/pricing/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-blue-700 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:bg-blue-50 transition"
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('prices.viewPdf')}
            </a>
          )}
          <Link
            to="/"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/15 backdrop-blur border-2 border-white/40 text-white rounded-xl font-semibold hover:bg-white/25 transition"
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {t('prices.bookNow')}
          </Link>
        </div>
      </div>
    </section>
  );

  // Title shows immediately; only the price content waits on the request.
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
        {hero}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            <p className="text-gray-600 font-semibold">{t('prices.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !pricing || pricing.enabled === false) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="p-4 bg-gray-100 rounded-full mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
          </svg>
        </div>
        <p className="text-gray-600">{t('prices.unavailable')}</p>
        <Link to="/" className="mt-4 text-blue-600 hover:text-blue-700 font-medium">
          {t('prices.backToBooking')}
        </Link>
      </div>
    );
  }

  const promo = pricing.promo;
  const notes = pricing.notes?.[language] || [];

  const priceCell = (item) => {
    const hasDiscount = item.discountPrice && item.discountPrice !== '-';
    const base = item.price && item.price !== '-' ? item.price : null;

    return (
      <div className={`flex items-baseline gap-2 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
        {base && (
          <span
            className={
              hasDiscount
                ? 'text-sm sm:text-base font-semibold text-gray-500 line-through decoration-2'
                : 'text-lg sm:text-xl font-extrabold text-gray-900'
            }
            dir="ltr"
          >
            {base} {currency}
          </span>
        )}
        {hasDiscount && (
          <span className="text-lg sm:text-2xl font-extrabold text-rose-600" dir="ltr">
            {item.discountPrice} {currency}
          </span>
        )}
        {!base && !hasDiscount && <span className="text-base font-bold text-gray-400">—</span>}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {hero}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-8 sm:space-y-10">

        {/* Category cards */}
        {pricing.categories?.map((category, ci) => {
          const accent = ACCENTS[category.accent] || ACCENTS.blue;
          const hasAnyDiscount = category.items?.some(i => i.discountPrice && i.discountPrice !== '-');

          return (
            <section
              key={ci}
              className={`bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition ${accent.ring}`}
            >
              <div className={`h-1.5 bg-gradient-to-r ${accent.bar}`} />
              <div className="p-4 sm:p-6 lg:p-8">
                <div className={`flex flex-wrap items-center justify-between gap-3 mb-5 pb-4 border-b border-gray-100`}>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
                    {label(category.title)}
                  </h2>
                  {hasAnyDiscount && (
                    <span className={`text-xs sm:text-sm font-extrabold px-3.5 py-1.5 rounded-full border-2 ${accent.chip}`}>
                      {t('prices.afterDiscount')}
                    </span>
                  )}
                </div>

                <ul className="divide-y divide-gray-100">
                  {category.items?.map((item, ii) => (
                    <li
                      key={ii}
                      className="py-3.5 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-6 hover:bg-gray-50/70 -mx-2 px-2 rounded-lg transition"
                    >
                      <span className="text-base sm:text-lg text-gray-900 font-semibold leading-relaxed">
                        {label(item.name)}
                      </span>
                      {priceCell(item)}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          );
        })}

        {/* National Day / seasonal promo */}
        {promo?.enabled && promo.items?.length > 0 && (
          <section className="relative rounded-2xl shadow-xl overflow-hidden bg-gradient-to-br from-emerald-700 via-green-700 to-emerald-800">
            <div className="absolute inset-0 opacity-[0.07]" aria-hidden="true">
              <div className="absolute top-0 end-0 w-64 h-64 rounded-full bg-white -translate-y-1/3 translate-x-1/3" />
              <div className="absolute bottom-0 start-0 w-80 h-80 rounded-full bg-white translate-y-1/3 -translate-x-1/3" />
            </div>

            <div className="relative p-5 sm:p-8 lg:p-10">
              <div className="text-center mb-6 sm:mb-8">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur rounded-full text-white text-sm sm:text-base font-extrabold mb-4 border-2 border-white/30">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l2.4 7.4H22l-6 4.4 2.3 7.2-6.3-4.6-6.3 4.6L7.9 13.8 2 9.4h7.6z" />
                  </svg>
                  {t('prices.limitedOffer')}
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2">
                  {label(promo.title)}
                </h2>
                <p className="text-emerald-50 text-base sm:text-lg font-bold">
                  {label(promo.subtitle)}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {promo.items.map((item, i) => (
                  <div
                    key={i}
                    className="bg-white/95 backdrop-blur rounded-xl p-4 sm:p-5 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 flex flex-col justify-between gap-3"
                  >
                    <p className="text-base sm:text-lg font-bold text-gray-900 leading-relaxed">
                      {label(item.name)}
                    </p>
                    <div className={`flex items-center justify-between gap-2 pt-2 border-t border-gray-100`}>
                      <span className="text-xs sm:text-sm text-gray-600 font-bold uppercase tracking-wide">{t('prices.packagePrice')}</span>
                      <span className="text-xl sm:text-2xl font-extrabold text-emerald-700" dir="ltr">
                        {item.price} {currency}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Notes */}
        {notes.length > 0 && (
          <section className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-5 sm:p-7">
            <ul className="space-y-3">
              {notes.map((note, i) => (
                <li key={i} className="flex items-start gap-3 text-base sm:text-lg text-gray-800 font-semibold leading-relaxed">
                  <svg className="w-5 h-5 mt-0.5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Closing CTA */}
        <section className="text-center py-4">
          <p className="text-gray-800 font-bold mb-4 text-lg sm:text-xl">{t('prices.ctaPrompt')}</p>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transition w-full sm:w-auto"
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {t('prices.bookNow')}
          </Link>
        </section>
      </div>
    </div>
  );
};

export default Prices;
